import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logoUrl from '../../src/styles/logo.png';
import { GoogleButton, LinkedInButton } from '../shared/GoogleButton';
import EmailCodeForm from '../shared/EmailCodeForm';
import { MAIN_ORIGIN } from '../../lib/domain';
import { handOffToApp } from '../../lib/authHandoff';

export default function LoginPage() {
  const { signIn } = useAuth();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || '/home';

  const params = new URLSearchParams(location.search);
  const authError = params.get('auth_error');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(authError ?? '');
  const [loading, setLoading] = useState(false);
  // `?code=1` : arrivée depuis une invitation dont le lien du mail a été brûlé
  // par la protection de messagerie du destinataire.
  const [mode, setMode] = useState<'password' | 'code'>(params.get('code') ? 'code' : 'password');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);
    if (error) {
      if (error.message === 'Invalid login credentials') {
        setError('Email ou mot de passe incorrect');
      } else {
        setError(error.message);
      }
    } else {
      await handOffToApp(from);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={logoUrl} alt="RevertAI Logo" className="h-10 w-auto object-contain" />
          <span className="font-bold text-2xl tracking-tight text-white">
            Revert<span className="text-amber-500">AI</span>
          </span>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl">
          <h1 className="text-xl font-semibold text-white mb-1">Connexion</h1>
          <p className="text-sm text-slate-400 mb-6">
            {mode === 'code'
              ? 'Recevez un code à usage unique par email'
              : 'Accédez à votre espace recrutement'}
          </p>

          {mode === 'code' ? (
            <EmailCodeForm
              initialEmail={email}
              onSuccess={() => handOffToApp(from)}
              onCancel={() => setMode('password')}
            />
          ) : (
            <>
              <div className="space-y-2">
                <GoogleButton label="Continuer avec Google" />
                {/* TODO: Activer quand la connexion LinkedIn est implémentée
                <LinkedInButton label="Continuer avec LinkedIn" />
                */}
              </div>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-700" />
                <span className="text-xs text-slate-500">ou par email</span>
                <div className="flex-1 h-px bg-slate-700" />
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-950/50 border border-red-800 rounded-lg px-4 py-3 mb-5 text-red-400 text-sm">
                  <AlertCircle size={16} className="shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail size={16} className="text-slate-500" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      placeholder="vous@exemple.com"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-slate-300">Mot de passe</label>
                    <Link to="/forgot-password" className="text-xs font-medium text-amber-500 hover:text-amber-400 transition-colors">
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock size={16} className="text-slate-500" />
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm mt-2"
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </button>
              </form>

              <button
                type="button"
                onClick={() => { setError(''); setMode('code'); }}
                className="w-full text-center text-sm text-amber-500 hover:text-amber-400 font-medium transition-colors mt-4"
              >
                Recevoir un code par email
              </button>
            </>
          )}

          <p className="text-center text-sm text-slate-500 mt-6">
            Pas encore de compte ?{' '}
            <a
              href={`${MAIN_ORIGIN}/#beta`}
              className="text-amber-500 hover:text-amber-400 font-medium transition-colors"
            >
              Rejoindre la bêta →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
